import { useEffect, useMemo, useRef, useState } from 'react';
import type { DateSlot } from '../../poller/types.js';
import { addToWatchlist, searchCatalogues, type SearchResult } from '../client.ts';
import { Thumb } from '../components/Thumb.tsx';
import { hasPassedLocally } from '../lib/localdate.js';
import type { WatchItem } from '../lib/model.js';
import { Peek } from './Peek.tsx';

interface Props {
  onAdded: () => void;
  /**
   * Everything this reader follows. Two jobs: a second tap on a row reads as
   * "added" rather than "add again", and a followed thing is opened through
   * its own page rather than peeked at — a peek is for what you do not have.
   */
  items: readonly WatchItem[];
  /**
   * Held by the app, not here. Opening a followed row replaces this whole
   * screen with its item page, so state kept here would not survive the way
   * back — you would return to an empty search box having typed nothing.
   */
  query: string;
  onQuery: (query: string) => void;
  facet: FacetId;
  onFacet: (facet: FacetId) => void;
  onOpenItem: (item: WatchItem) => void;
}

type AddState = 'idle' | 'adding' | 'added';

/**
 * The tabs.
 *
 * Not a filter for tidiness — it is what makes the *series* reachable. Asking
 * "will there be another Rivers of London" is a different question from
 * "when is Amongst Our Weapons out", and until the second kind of row has a
 * place of its own, the first kind buries it: there are ten books called
 * Rivers of London-something and exactly one series.
 *
 * `all` stays the default because ranking already does the work when you know
 * what you want.
 */
export type FacetId = 'all' | 'books' | 'screen' | 'series' | 'people';

interface Facet {
  id: FacetId;
  label: string;
  match: (r: SearchResult) => boolean;
  /** Said when the tab is chosen and empty — each absence means something. */
  empty: string;
}

const FACETS: readonly Facet[] = [
  { id: 'all', label: 'All', match: () => true, empty: 'Nothing found.' },
  {
    id: 'books',
    label: 'Books',
    match: (r) => r.kind === 'title' && r.medium === 'book',
    empty: 'No books under that name.',
  },
  {
    id: 'screen',
    label: 'Film & TV',
    match: (r) => r.kind === 'title' && r.medium !== 'book',
    empty: 'Nothing on screen under that name.',
  },
  {
    id: 'series',
    label: 'Series',
    match: (r) => r.kind === 'series',
    empty: 'No series under that name — try the first book’s title.',
  },
  {
    id: 'people',
    label: 'People',
    match: (r) => r.kind === 'creator',
    empty: 'Nobody under that name with more than one book.',
  },
];

function kindLabel(result: SearchResult): string {
  if (result.kind === 'creator') return result.medium === 'book' ? 'Author' : 'Director';
  if (result.kind === 'series') return 'Series';
  if (result.medium === 'film') return 'Film';
  if (result.medium === 'tv') return 'TV';
  return 'Book';
}

export function Add({ onAdded, items, query, onQuery, facet, onFacet, onOpenItem }: Props) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [degraded, setDegraded] = useState<string[]>([]);
  const [state, setState] = useState<Record<string, AddState>>({});
  const [status, setStatus] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [peekKey, setPeekKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const followed = useMemo(() => {
    const map = new Map<string, WatchItem>();
    for (const item of items) map.set(`${item.source}:${item.kind}:${item.sourceId}`, item);
    return map;
  }, [items]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced so typing "dune" is one search, not four.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setDegraded([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchCatalogues(trimmed, controller.signal);
        setResults(response.results);
        setDegraded(response.degraded ?? []);
        setStatus(null);
      } catch (error) {
        if (!controller.signal.aborted) {
          setStatus(error instanceof Error ? error.message : 'Search failed');
        }
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query]);

  const counts = useMemo(() => {
    const out: Record<FacetId, number> = { all: 0, books: 0, screen: 0, series: 0, people: 0 };
    for (const facetDef of FACETS) {
      out[facetDef.id] = results.filter(facetDef.match).length;
    }
    return out;
  }, [results]);

  const active = FACETS.find((f) => f.id === facet) ?? (FACETS[0] as Facet);
  const shown = results.filter(active.match);
  // One clock for the whole list, so two rows on the same screen cannot
  // disagree about what day it is. Recomputed per render, which is what a
  // search screen wants — the results are new each time anyway.
  const now = new Date();
  // Rendered instead of the results, not beside them — this component stays
  // mounted underneath, so the query and the results survive the trip.
  const peeked = peekKey ? (results.find((r) => resultKey(r) === peekKey) ?? null) : null;

  // Not the raw setter: a stale error from a failed `+` tap on a different row
  // must not greet the reader on the peek they open next.
  function openPeek(key: string) {
    setStatus(null);
    setPeekKey(key);
  }

  async function add(result: SearchResult, options: { formats: DateSlot[]; region: string | null }) {
    const key = resultKey(result);
    setState((s) => ({ ...s, [key]: 'adding' }));
    try {
      await addToWatchlist(result, { formats: options.formats, region: options.region });
      setState((s) => ({ ...s, [key]: 'added' }));
      setPeekKey(null);
      onAdded();
    } catch (error) {
      setState((s) => ({ ...s, [key]: 'idle' }));
      setStatus(error instanceof Error ? error.message : 'Could not add that');
    }
  }

  if (peeked) {
    const key = resultKey(peeked);
    return (
      <Peek
        result={peeked}
        now={now}
        followed={followed}
        busy={state[key] === 'adding'}
        status={status}
        onBack={() => setPeekKey(null)}
        onAdd={(formats, region) => void add(peeked, { formats, region })}
        onOpenItem={onOpenItem}
      />
    );
  }

  return (
    <>
      <div className="searchbar">
        <span className="glyph" aria-hidden="true">
          ⌕
        </span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search books, films, people…"
          aria-label="Search"
          autoCapitalize="none"
          autoCorrect="off"
        />
      </div>

      {results.length > 0 && (
        <div className="facets" role="tablist" aria-label="Kind of result">
          {FACETS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={facet === f.id}
              className={facet === f.id ? 'facet on' : 'facet'}
              // A tab that would be empty is not offered. "Series (0)" invites
              // a tap that answers nothing.
              disabled={counts[f.id] === 0}
              onClick={() => onFacet(f.id)}
            >
              {f.label}
              <span className="facet-count">{counts[f.id]}</span>
            </button>
          ))}
        </div>
      )}

      {results.length === 0 && (
        <p className="hint">
          Books, films, series, and the people who make them. Add the <em>series</em> if what you
          want to know is whether there will be another one — it needs no book to exist yet.
        </p>
      )}

      {status && <p className="notice">{status}</p>}

      {/* "No books found" and "the book search is down" are different answers
          to the same query, and conflating them is how you lose trust. */}
      {degraded.length > 0 && (
        <p className="notice">
          {degraded.includes('hardcover') ? 'Book search' : 'Film search'} is unavailable right
          now — these results are incomplete.
        </p>
      )}

      {results.length === 0 && query.trim().length >= 2 && !searching && !status && (
        <p className="hint">Nothing found. Try the author's name, or the exact title.</p>
      )}

      {results.length > 0 && shown.length === 0 && <p className="hint">{active.empty}</p>}

      <ul className="results">
        {shown.map((result) => {
          const key = resultKey(result);
          const mine = followed.get(key) ?? null;
          const added = state[key] === 'added' || mine !== null;
          const adding = state[key] === 'adding';

          return (
            <li key={key}>
              <div className="result">
                <Thumb
                  src={result.imageUrl}
                  kind={result.kind}
                  medium={result.medium}
                  size="sm"
                />
                {/* The row opens a peek; the + adds it as it stands. Capture
                    stays one tap for anyone who doesn't care. */}
                <button
                  type="button"
                  className="result-main"
                  onClick={() => (mine ? onOpenItem(mine) : openPeek(key))}
                >
                  <span className="result-title">{result.title}</span>
                  <span className="result-meta">{metaLine(result, now)}</span>
                  <span className="chiprow">
                    <span className={result.kind === 'creator' ? 'kindtag person' : 'kindtag'}>
                      {kindLabel(result)}
                    </span>
                    {result.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="kindtag genre">
                        {tag}
                      </span>
                    ))}
                  </span>
                </button>
                <button
                  type="button"
                  className={added ? 'add-btn done' : 'add-btn'}
                  disabled={added || adding}
                  aria-label={added ? `${result.title} added` : `Add ${result.title}`}
                  onClick={() => add(result, { formats: [], region: null })}
                >
                  {added ? '✓' : '+'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function resultKey(result: SearchResult): string {
  return `${result.source}:${result.kind}:${result.sourceId}`;
}

/**
 * `result.alreadyOut` is the worker's answer, judged in UTC, and it is right
 * for what the worker uses it for — ranking, where recency is a nudge and
 * nobody is being promised anything. This line *is* a promise about where the
 * thing will land, and `deriveHome` keeps that promise in the reader's own
 * calendar. Believing the worker here made the note wrong for the last hours
 * of a western reader's day: "out now · goes to your backlog", and then it
 * appeared on the slate as still upcoming. Same date, same reader, judged
 * twice.
 */
function metaLine(result: SearchResult, now: Date): string {
  const bits: string[] = [];
  if (result.byline) bits.push(result.byline);
  if (result.year) bits.push(String(result.year));
  // Said plainly at the point of adding, so nothing lands on the slate
  // unexpectedly — there is no age limit, it just goes to the backlog.
  if (result.releaseDate && hasPassedLocally(result.releaseDate, now))
    bits.push('out now · goes to your backlog');
  else if (result.kind === 'creator') bits.push('new work as it is listed');
  else if (result.kind === 'series') bits.push('watch for the next one');
  return bits.join(' · ');
}
